'use client';

import { useCallback, useMemo, useState } from 'react';
import { getCoreRowModel, Table, useReactTable, VisibilityState } from '@tanstack/react-table';
import { FilterIcon, RefreshCwIcon, TrashIcon, XIcon } from 'lucide-react';

import { CUSTOMER_COLUMNS } from '@/app/(app)/customers/table/columns';
import { useCustomersTableCtx } from '@/app/(app)/customers/table/provider';
import { Popover } from '@/components/atoms/popover';
import { Select } from '@/components/atoms/select';
import { ColumnVisibilityPicker, DataTable } from '@/components/data-table';
import { ApiSchemas } from '@/components/providers/v1-api';
import { useCustomers } from '@/components/providers/v1-cutsomer-provider';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

type ApiCustomer = ApiSchemas['CustomerRead'];

export function CustomerTable() {
  const { ids, selectedIds, setSelectedIds } = useCustomersTableCtx();

  const customers = useCustomers(ids);
  const rowSelection = useMemo(() => {
    const selected: Record<string, boolean> = {};
    for (const id of selectedIds) {
      selected[id.toString()] = true;
    }
    return selected;
  }, [selectedIds]);
  const onRowSelect = useCallback(
    (v: Record<string, boolean>) => {
      setSelectedIds(Object.keys(v));
    },
    [setSelectedIds]
  );

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    status: false,
    email: false,
  });

  const table = useReactTable({
    data: customers,
    columns: CUSTOMER_COLUMNS,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    getRowId: (row) => row.id.toString(),
    state: {
      columnVisibility,
      rowSelection,
    },
    onRowSelectionChange: (updaterOrValue) => {
      if (typeof updaterOrValue === 'function') {
        onRowSelect(updaterOrValue(rowSelection));
      } else {
        onRowSelect(updaterOrValue);
      }
    },
  });

  return (
    <div className='space-y-3'>
      {selectedIds.length > 0 ? <HeaderSelectedActions /> : <Header table={table} />}
      <div className='w-full overflow-hidden rounded-md border'>
        <DataTable table={table} columns={CUSTOMER_COLUMNS} emptyMessage='No customers' />
      </div>
    </div>
  );
}

interface HeaderProps {
  table: Table<ApiCustomer>;
}

function Header({ table }: HeaderProps) {
  const { isFetching, refetch } = useCustomersTableCtx();

  return (
    <div className='flex w-full items-center justify-between gap-8'>
      <div className='flex w-full max-w-sm items-center gap-2'>
        <Filters />
      </div>

      <div className='flex items-center gap-2'>
        <ColumnVisibilityPicker table={table} />
        <Button variant='outline' size='icon' disabled={isFetching} onClick={refetch}>
          <RefreshCwIcon className={cn(isFetching && 'animate-spin')} />
        </Button>
      </div>
    </div>
  );
}

function Filters() {
  const [isOpen, setIsOpen] = useState(false);
  const { filters, setFilters } = useCustomersTableCtx();
  const [name, setName] = useState(filters.name_query);
  const [phone, setPhone] = useState(filters.phone_query);
  const [address, setAddress] = useState(filters.address_query);
  const [status, setStatus] = useState(filters.status);
  const [externalId, setExternalId] = useState(filters.external_id);
  const isFiltering = useMemo(() => {
    return (
      !!filters.name_query ||
      !!filters.phone_query ||
      !!filters.address_query ||
      filters.status !== 'ACTIVE' ||
      !!filters.external_id
    );
  }, [filters]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        setFilters({ ...filters, name_query: name, phone_query: phone, external_id: externalId, page: 1 });
        setIsOpen(false);
      }
    },
    [filters, name, phone, externalId, setFilters]
  );

  const onClear = useCallback(() => {
    setName('');
    setPhone('');
    setAddress('');
    setExternalId('');
    setFilters({
      ...filters,
      name_query: '',
      phone_query: '',
      address_query: '',
      status: 'ACTIVE',
      external_id: '',
      page: 1,
    });
  }, [filters, setFilters]);

  const onApply = useCallback(() => {
    setFilters({
      ...filters,
      name_query: name,
      phone_query: phone,
      address_query: address,
      status: status,
      external_id: externalId,
      page: 1,
    });
  }, [filters, name, phone, address, status, externalId, setFilters]);

  return (
    <>
      <Popover
        trigger={
          <Button variant='outline' size='sm'>
            <FilterIcon className='size-4' />
            <span>Filters</span>
          </Button>
        }
        className='w-[400px] space-y-4 p-4'
        isOpen={isOpen}
        onOpenChange={setIsOpen}
      >
        <div className='flex w-full items-center'>
          <Label className='w-1/2'>Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='Customer name'
            onKeyDown={onKeyDown}
            className='w-full'
          />
        </div>
        <div className='flex w-full items-center'>
          <Label className='w-1/2'>Phone Number</Label>
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder='Phone number'
            onKeyDown={onKeyDown}
          />
        </div>
        <div className='flex w-full items-center'>
          <Label className='w-1/2'>Address</Label>
          <Input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder='Address'
            onKeyDown={onKeyDown}
            className='w-full'
          />
        </div>

        <div className='flex w-full items-center'>
          <Label className='w-1/2'>FieldRoutes</Label>
          <Input
            value={externalId}
            onChange={(e) => setExternalId(e.target.value)}
            placeholder='Account number'
            onKeyDown={onKeyDown}
            className='w-full'
          />
        </div>

        <div className='flex w-full items-center'>
          <Label className='w-1/2'>Status</Label>
          <Select
            options={[
              { label: 'Active', value: 'ACTIVE' },
              { label: 'Inactive', value: 'INACTIVE' },
              { label: 'All', value: 'ALL' },
            ]}
            value={status ?? 'ALL'}
            onChange={(v) => {
              if (v === 'ALL') {
                setStatus(null);
              } else {
                setStatus(v);
              }
            }}
            className='w-full'
          />
        </div>

        <div className='flex justify-end gap-2'>
          <Button
            variant='outline'
            onClick={() => {
              onClear();
              setIsOpen(false);
            }}
            size='sm'
          >
            Clear
          </Button>
          <Button
            variant='default'
            onClick={() => {
              onApply();
              setIsOpen(false);
            }}
            size='sm'
          >
            Apply
          </Button>
        </div>
      </Popover>
      {isFiltering && (
        <Button variant='ghost' onClick={onClear} size='sm'>
          Clear
        </Button>
      )}
    </>
  );
}

function HeaderSelectedActions() {
  const { selectedIds, setSelectedIds } = useCustomersTableCtx();
  return (
    <div className='flex items-center justify-between gap-2'>
      <div className='flex items-center gap-2'>
        <div className='flex h-9 items-center rounded-md border border-dashed bg-background shadow'>
          <div className='px-4 text-sm font-medium text-foreground/70'>{selectedIds.length} selected</div>
          <Button
            variant='none'
            size='icon'
            className='rounded-l-none border-l border-dashed hover:bg-accent'
            onClick={() => setSelectedIds([])}
          >
            <XIcon className='h-4 w-4' />
          </Button>
        </div>
      </div>
      <SelectedActionDeleteCustomers />
    </div>
  );
}

function SelectedActionDeleteCustomers() {
  const { selectedIds } = useCustomersTableCtx();

  // const { api } = useApiV1Ctx();
  // const toastId = useRef<number | string | undefined>(undefined);

  // const { mutate } = api.deleteCustomersApiV1CrmCustomersDeletePost.useMutation(
  //   {},
  //   {
  //     onSuccess: () => {
  //       toast.success('Customers deleted', {
  //         id: toastId.current,
  //       });
  //       toastId.current = undefined;
  //       setSelectedIds([]);
  //       refetch();
  //     },
  //   }
  // );

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant='destructive' className='gap-3'>
          <TrashIcon className='h-4 w-4' />
          Delete ({selectedIds.length})
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Customers</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          Are you sure you want to delete these customers? This action cannot be undone.
        </DialogDescription>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Cancel</Button>
          </DialogClose>
          <DialogClose asChild>
            <Button variant='destructive' onClick={() => {}}>
              Delete
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
